/** RTK Query endpoints for project-level user flows and their versions. */
/* eslint-disable max-lines-per-function */
import { eventService } from '../../services/eventService';

import { api } from './api';

export interface UserFlow {
  id: string;
  persona: string;
  mermaid_content: string;
  render_order?: string | null;
  version: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserFlowsResponse {
  project_id: string;
  user_flows: UserFlow[];
}

export interface FlowVersionEntry {
  version: number;
  created_at: string | null;
  is_current: boolean;
}

interface UserFlowIdentity {
  projectId: string;
  flowId: string;
}

interface RestoreUserFlowVersionArgs extends UserFlowIdentity {
  version: number;
}

export interface UpdateUserFlowArgs {
  projectId: string;
  persona: string;
  mermaidContent: string;
}

interface UpdateUserFlowResponse {
  project_id: string;
  id: string;
  persona: string;
  version: number;
}

export interface UserFlowProposedOp {
  op: 'add' | 'update' | 'delete';
  id?: string;
  persona?: string;
  content?: string;
  rationale?: string;
}

export interface UserFlowPendingProposalResponse {
  has_pending: boolean;
  operations: UserFlowProposedOp[];
  summary?: string | null;
  source?: string | null;
  created_at?: string | null;
}

export const userFlowApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUserFlows: builder.query<UserFlowsResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/user-flows`,
      providesTags: (result, _error, projectId) => [
        { type: 'UserFlow' as const, id: projectId },
        ...(result?.user_flows ?? []).map((flow) => ({
          type: 'UserFlow' as const,
          id: `${projectId}:${flow.id}`,
        })),
      ],
      async onCacheEntryAdded(projectId, { cacheEntryRemoved, dispatch }) {
        eventService.connect(projectId);
        const unsubscribe = eventService.subscribe('user_flows', (data: unknown) => {
          const payload = data as Partial<UserFlowsResponse>;

          if (payload.project_id !== projectId) return;
          if (!Array.isArray(payload.user_flows)) {
            dispatch(api.util.invalidateTags([{ type: 'UserFlow', id: projectId }]));

            return;
          }

          dispatch(userFlowApi.util.upsertQueryData('getUserFlows', projectId, {
            project_id: projectId,
            user_flows: payload.user_flows,
          }));
          dispatch(api.util.invalidateTags([{ type: 'UserFlowVersion' }]));
        });

        try {
          await cacheEntryRemoved;
        } finally {
          unsubscribe();
        }
      },
    }),
    updateUserFlow: builder.mutation<UpdateUserFlowResponse, UpdateUserFlowArgs>({
      query: ({ projectId, persona, mermaidContent }) => ({
        url: `/api/v1/projects/${projectId}/user-flows`,
        method: 'PUT',
        body: { persona, mermaid_content: mermaidContent },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'UserFlow' as const, id: projectId },
      ],
    }),
    deleteUserFlow: builder.mutation<{ deleted: boolean }, UserFlowIdentity>({
      query: ({ projectId, flowId }) => ({
        url: `/api/v1/projects/${projectId}/user-flows/${flowId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ projectId, flowId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(userFlowApi.util.updateQueryData('getUserFlows', projectId, (draft) => {
          draft.user_flows = draft.user_flows.filter((flow) => flow.id !== flowId);
        }));

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, error, { projectId, flowId }) => (
        error
          ? [{ type: 'UserFlow' as const, id: projectId }]
          : [{ type: 'UserFlowVersion' as const, id: `${projectId}:${flowId}` }]
      ),
    }),
    getUserFlowVersions: builder.query<FlowVersionEntry[], UserFlowIdentity>({
      query: ({ projectId, flowId }) => (
        `/api/v1/projects/${projectId}/user-flows/${flowId}/versions`
      ),
      transformResponse: (response: { versions: FlowVersionEntry[] }) => response.versions,
      providesTags: (_result, _error, { projectId, flowId }) => [
        { type: 'UserFlowVersion' as const, id: `${projectId}:${flowId}` },
      ],
    }),
    restoreUserFlowVersion: builder.mutation<{ restored: boolean; version: number }, RestoreUserFlowVersionArgs>({
      query: ({ projectId, flowId, version }) => ({
        url: `/api/v1/projects/${projectId}/user-flows/${flowId}/versions/${version}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId, flowId }) => [
        { type: 'UserFlow' as const, id: projectId },
        { type: 'UserFlowVersion' as const, id: `${projectId}:${flowId}` },
      ],
    }),
    getPendingUserFlowProposal: builder.query<UserFlowPendingProposalResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/user-flows/pending`,
      providesTags: (_result, _error, projectId) => [
        { type: 'UserFlowProposal' as const, id: projectId },
      ],
    }),
    proposeUserFlowEdit: builder.mutation<{ status: string }, { projectId: string; prompt: string }>({
      query: ({ projectId, prompt }) => ({
        url: `/api/v1/projects/${projectId}/user-flows/propose`,
        method: 'POST',
        body: { prompt },
      }),
    }),
    acceptPendingUserFlowProposal: builder.mutation<{ applied: unknown }, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/api/v1/projects/${projectId}/user-flows/pending/accept`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'UserFlowProposal' as const, id: projectId },
      ],
    }),
    rejectPendingUserFlowProposal: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `/api/v1/projects/${projectId}/user-flows/pending/reject`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, projectId) => [
        { type: 'UserFlowProposal' as const, id: projectId },
      ],
    }),
  }),
});

export const selectUserFlows = (response: UserFlowsResponse | undefined): UserFlow[] => (
  response?.user_flows ?? []
);

export const {
  useDeleteUserFlowMutation,
  useGetUserFlowsQuery,
  useLazyGetUserFlowsQuery,
  useGetUserFlowVersionsQuery,
  useLazyGetUserFlowVersionsQuery,
  useRestoreUserFlowVersionMutation,
  useUpdateUserFlowMutation,
  useGetPendingUserFlowProposalQuery,
  useProposeUserFlowEditMutation,
  useAcceptPendingUserFlowProposalMutation,
  useRejectPendingUserFlowProposalMutation,
} = userFlowApi;
